import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ClipboardList, Check, X, Trash2, Mail, Phone, Briefcase, Monitor, GraduationCap, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS = {
  pendiente: { label: 'Pendiente', plural: 'pendientes', cls: 'bg-amber-100 text-amber-700' },
  aprobado: { label: 'Dado de alta', plural: 'dadas de alta', cls: 'bg-emerald-100 text-emerald-700' },
  rechazado: { label: 'Rechazado', plural: 'rechazadas', cls: 'bg-red-100 text-red-700' },
};

const Preinscriptions = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('pendiente');
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/preinscriptions`);
      setItems(res.data || []);
    } catch (e) {
      toast.error('Error al cargar las preinscripciones');
    }
  };
  useEffect(() => { load(); }, []);

  const approve = async (p) => {
    setBusy(p.id);
    try {
      const res = await axios.post(`${API_URL}/preinscriptions/${p.id}/approve`);
      toast.success(res.data?.credentials_sent ? 'Alumno dado de alta. Se enviaron las credenciales por correo.' : 'Alumno dado de alta correctamente.');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al dar de alta');
    } finally { setBusy(null); }
  };
  const reject = async (p) => {
    setBusy(p.id);
    try { await axios.post(`${API_URL}/preinscriptions/${p.id}/reject`); toast.success('Preinscripción rechazada'); load(); }
    catch (e) { toast.error('Error al rechazar'); } finally { setBusy(null); }
  };
  const remove = async (p) => {
    if (!window.confirm('¿Eliminar esta preinscripción?')) return;
    setBusy(p.id);
    try { await axios.delete(`${API_URL}/preinscriptions/${p.id}`); toast.success('Eliminada'); load(); }
    catch (e) { toast.error('Error al eliminar'); } finally { setBusy(null); }
  };

  const exportCsv = async () => {
    try {
      const params = filter !== 'todas' ? { status: filter } : {};
      const res = await axios.get(`${API_URL}/preinscriptions/export`, { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `preinscripciones_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('No se pudo exportar'); }
  };

  const filtered = items.filter((i) => filter === 'todas' || i.status === filter);
  const pendingCount = items.filter((i) => i.status === 'pendiente').length;

  return (
    <Layout>
      <div className="space-y-6" data-testid="preinscriptions-page">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#004E89]/10 flex items-center justify-center">
            <ClipboardList className="w-5 h-5 text-[#004E89]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Preinscripciones</h1>
            <p className="text-sm text-slate-500">Solicitudes recibidas desde los formularios públicos de las formaciones</p>
          </div>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={items.length === 0}
            className="ml-auto border-[#004E89] text-[#004E89] hover:bg-[#004E89] hover:text-white" data-testid="export-preinscriptions">
            <Download className="w-4 h-4 mr-1" /> Exportar CSV
          </Button>
        </div>

        <div className="flex flex-wrap gap-2">
          {['pendiente', 'aprobado', 'rechazado', 'todas'].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${filter === f ? 'bg-[#004E89] text-white border-[#004E89]' : 'bg-white text-slate-600 border-slate-200'}`}
              data-testid={`filter-${f}`}>
              {f === 'todas' ? 'Todas' : STATUS[f].label}{f === 'pendiente' && pendingCount > 0 ? ` (${pendingCount})` : ''}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="bg-white rounded-lg border border-slate-200 p-10 text-center text-slate-400">
            No hay preinscripciones {filter !== 'todas' ? STATUS[filter]?.plural : ''}.
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((p) => (
              <div key={p.id} className="bg-white rounded-lg border border-slate-200 p-4" data-testid="preinscription-item">
                <div className="flex flex-col md:flex-row md:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-slate-900">{p.nombre} {p.apellidos}</p>
                      <Badge className={STATUS[p.status]?.cls}>{STATUS[p.status]?.label}</Badge>
                      <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                        {p.course_type === 'online' ? <Monitor className="w-3.5 h-3.5" /> : <GraduationCap className="w-3.5 h-3.5" />}
                        {p.course_name}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500 mt-1">
                      <span>DNI: {p.documento_identidad}</span>
                      <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {p.email}</span>
                      <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {p.telefono}</span>
                      <span className="inline-flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> {p.categoria_profesional}</span>
                      <span>Centro: {p.centro_trabajo}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {p.status === 'pendiente' && (
                      <>
                        <Button size="sm" onClick={() => approve(p)} disabled={busy === p.id} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pre-approve">
                          <Check className="w-4 h-4 mr-1" /> Dar de alta
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => reject(p)} disabled={busy === p.id} className="border-red-200 text-red-600 hover:bg-red-50" data-testid="pre-reject">
                          <X className="w-4 h-4 mr-1" /> Rechazar
                        </Button>
                      </>
                    )}
                    <button onClick={() => remove(p)} disabled={busy === p.id} className="p-2 text-slate-400 hover:text-red-600" title="Eliminar" data-testid="pre-delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Preinscriptions;
